import { css, type Component } from "dreamland/core";
import { Link } from "dreamland/router";
import { Account, CustomEmoji, Status } from "../api";
import { processContent } from "../utils/content";
import humanizeDuration from "humanize-duration";
import { bookmark, favourite, reblog, unbookmark, unfavourite, unreblog } from "../api/statuses";
import { getAccount } from "../api/accounts";

const StatusView: Component<{status: Status}, {actualStatus: Status|undefined, booster: Account|undefined, privacyIcon: string, replyingToAccount: Account|undefined}> = function (cx) {
    this.privacyIcon = "";
    cx.init = async() => {
        if (this.status.reblog) {
            this.booster = this.status.account;
            this.actualStatus = this.status.reblog;
        } else {
            this.actualStatus = this.status;
        }
        if (this.actualStatus.in_reply_to_account_id) {
            this.replyingToAccount = await getAccount(this.actualStatus.in_reply_to_account_id);
        }
        switch (this.actualStatus.visibility) {
            case "public":
                this.privacyIcon = "public"; // globe icon
                break;
            case "unlisted":
                this.privacyIcon = "bedtime"; // open lock icon
                break;
            case "private":
                this.privacyIcon = "lock"; // closed lock icon
                break;
            case "direct":
                this.privacyIcon = "3p"; // mail icon
                break;
            default:
                this.privacyIcon = "";
        }
    }

	return (
        <div>
            {use(this.actualStatus).map(status => status ?  (
            <>
            <div class="status-header">
                {status.pinned ? (
                    <p class="status-pinned">
                    <span class="material-symbols-sharp">push_pin</span>
                    Pinned
                    </p>
                ) : null}
                {use(this.replyingToAccount).map(replyingToAccount => replyingToAccount ? (
                    <p class="status-replying-to" style="font-size: 0.85em; color: #555; margin-bottom: 0.5em;">
                        <span class="material-symbols-sharp" style="font-size: 1em; vertical-align: middle;">reply</span>
                        Replying to @{replyingToAccount.acct}
                    </p>
                ) : null)}
                {use(this.booster).map(booster => booster ? (
                    <p class="status-boosted-by">
                        <img src={booster.avatar} alt="Avatar" class="custom-emoji" />
                        <Link href={`/user/${booster.id}`}>
                        <span attr:innerHTML={processContent(booster.display_name || booster.username, booster.emojis)}></span>
                        </Link>
                        boosted
                    </p>
                ) : null)}
                <div class="status-author-info">
                <img src={status.account.avatar} alt="Avatar" class="status-avatar" />
                <Link href={`/user/${status.account.id}`} class="nostyle">
                    <span class="status-author" >
                        <p class="status-display-name" attr:innerHTML={processContent(status.account.display_name || status.account.username, status.account.emojis)}></p>
                        <p class="status-acct" style="color: #888; font-size: 0.9em;">@{status.account.acct}</p>
                    </span>
                </Link>
                </div>
            </div>
            <div class="status-content" attr:innerHTML={processContent(status.content, status.emojis)}></div>
            <div class="status-media">
                {status.media_attachments.map((media) => {
                    switch (media.type) {
                        case "image":
                            return <img src={media.preview_url} alt={media.description || "Media attachment"} />;
                        case "video":
                        case "audio":
                            return (
                                <video controls>
                                    <source src={media.url} />
                                    Your browser does not support the video tag.
                                </video>
                            );
                        default:
                            return null;
                    }
                })}
            </div>
            <div class="status-bottom">
            <ul class="status-actions">
                <li>
                    <button class="nostyle material-symbols-sharp" on:click={() => {
                        alert("todo: reply functionality");
                    }}>
                        reply
                    </button>
                </li>
                <li>
                    <button class="nostyle material-symbols-sharp" activated={status.reblogged} on:click={() => {
                        if (!status.reblogged) {
                            reblog(status.id);
                        } else {
                            unreblog(status.id);
                        }
                        status.reblogged = !status.reblogged;
                    }}>
                        repeat
                    </button>
                </li>
                <li>
                    <button class="nostyle material-symbols-sharp" activated={status.favourited} on:click={() => {
                        if (!status.favourited) {
                            favourite(status.id);
                        } else {
                            unfavourite(status.id);
                        }
                        status.favourited = !status.favourited;
                    }}>
                        star
                    </button>
                </li>
                <li>
                    <button class="nostyle material-symbols-sharp" disabled={status.reactions?.find(r => r.me) !== undefined} on:click={() => {
                        alert("todo: reactions and emoji picker component");
                    }}>
                        add_reaction
                    </button>
                </li>
                <li>
                    <button class="nostyle material-symbols-sharp" activated={status.bookmarked} on:click={() => {
                        if (!status.bookmarked) {
                            bookmark(status.id);
                        } else { 
                            unbookmark(status.id);
                        }
                        status.bookmarked = !status.bookmarked;
                    }}>
                        bookmark
                    </button>
                </li>
            </ul>
            <span class="status-timestamp">
                <span class="material-symbols-sharp" style="">{use(this.privacyIcon)}</span>
                <span>•</span>
                {humanizeDuration.humanizer({
                    language: "shortEn",
                    spacer: "",
                    largest: 2, 
                    languages: {
                      shortEn: {
                        y: () => "y",
                        mo: () => "mo",
                        w: () => "w",
                        d: () => "d",
                        h: () => "h",
                        m: () => "m",
                        s: () => "s",
                        ms: () => "ms",
                      },
                    },
                    maxDecimalPoints: 0,
                  })(Date.now() - new Date(status.created_at).getTime())
            }</span>
            </div>
            </>
            ) : null)}
        </div>
	);
};

StatusView.style = css`
    :scope {
        padding: 0.25em 0.5em;
    }
    .status-avatar {
        width: 2em;
        height: 2em;
        object-fit: cover;
    }
    .status-author-info {
        display: flex;
        align-items: center;
        gap: 0.5em;
        margin-bottom: 0.5em;
    }
    .status-author-info p {
        margin: 0;
    }
    .status-display-name {
        font-weight: 500;
    }
    .status-acct {
        color: #888;
        font-size: 0.9em;
    }
    .status-pinned,
    .status-boosted-by {
        font-size: 0.85em;
        color: #555;
        display: flex;
        align-items: center;
        gap: 0.25em;
        margin: 0 0 0.5em 0;
    }
    .status-content p {
        margin: 0 0 1em 0;
    }
    .status-content p:last-child {
        margin-bottom: 0;
    }
    .status-media {
        margin-top: 0.5em;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5em;
    }
    .status-media img, .status-media video {
        max-width: 100%;
    }
    .status-bottom {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 0.5em;
    }
    .status-actions {
        list-style: none;
        display: flex;
        gap: 1em;
        padding: 0;
        margin: 0;
    }
    .status-actions li button {
        font-size: 1.25em;
        color: #555;
        font-variation-settings: 'FILL' 0;
    }
    .status-actions li button[activated="true"] {
        color: #000;
        font-variation-settings: 'FILL' 1;
    }

    .status-timestamp {
        font-size: 0.85em;
        color: #888;
        display: flex;
        align-items: center;
        gap: 0.25em;
    }

    .status-timestamp .material-symbols-sharp {
        font-size: 1.1em;
        color: #444;
    }
`

export default StatusView;